import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { trackVendorSignupStart } from "@/services/metaPixel";
import {
  Smartphone,
  ShoppingCart,
  ChevronRight,
  Check,
  MapPin,
  Globe,
  Wallet,
  Clock,
} from "lucide-react";

const VENDOR_TYPES = [
  {
    id: "digital",
    title: "Vendedor Digital",
    subtitle: "Opera desde el celular",
    description: "Toma pedidos desde cualquier lugar. Los clientes reciben entregas desde central.",
    icon: Smartphone,
    color: "#3B82F6",
    bgColor: "#EFF6FF",
    features: [
      { icon: Globe, text: "Vende desde cualquier lugar" },
      { icon: Wallet, text: "Sin inventario físico" },
      { icon: Clock, text: "Horarios flexibles" },
    ],
    recommended: false,
  },
  {
    id: "carrito",
    title: "Vendedor Carrito",
    subtitle: "Punto físico o carrito en calle",
    description: "Vendes directamente con un carrito de productos en tu zona asignada.",
    icon: ShoppingCart,
    color: "#C8902A",
    bgColor: "#FEF3E2",
    features: [
      { icon: MapPin, text: "Zona de venta exclusiva" },
      { icon: ShoppingCart, text: "Carrito y productos incluidos" },
      { icon: Wallet, text: "Mayores comisiones" },
    ],
    recommended: true,
  },
];

export default function RegistroTipo() {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState(null);

  useEffect(() => {
    // Check if user came from previous step
    const method = sessionStorage.getItem('registro_method');
    if (!method) {
      navigate("/registro-vendedor/acceso");
    }
  }, [navigate]);

  const handleContinue = () => {
    if (selectedType) {
      // Track vendor signup start
      trackVendorSignupStart(selectedType);
      sessionStorage.setItem('registro_tipo', selectedType);
      navigate("/registro-vendedor/datos");
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#4A2C0A] font-bold">
          ¿Cómo quieres vender?
        </h1>
        <p className="font-['Poppins'] text-[#4A2C0A]/70">
          Elige el tipo de vendedor que mejor se adapte a ti
        </p>
      </div>

      {/* Type Cards */}
      <div className="grid gap-4">
        {VENDOR_TYPES.map((type) => {
          const isSelected = selectedType === type.id;
          const Icon = type.icon;

          return (
            <button
              key={type.id}
              onClick={() => setSelectedType(type.id)}
              className={`relative w-full text-left p-5 rounded-2xl border-2 transition-all duration-300 ${
                isSelected
                  ? `border-[${type.color}] bg-white shadow-lg ring-4 ring-[${type.color}]/10`
                  : "border-[#E5D4B3] bg-white/60 hover:bg-white hover:border-[#C8902A]/50"
              }`}
              style={{
                borderColor: isSelected ? type.color : undefined,
                boxShadow: isSelected ? `0 4px 20px ${type.color}20` : undefined,
              }}
              data-testid={`tipo-${type.id}`}
            >
              {/* Recommended Badge */}
              {type.recommended && (
                <div 
                  className="absolute -top-3 right-4 px-3 py-1 rounded-full text-xs font-medium text-white"
                  style={{ backgroundColor: type.color }}
                >
                  Recomendado
                </div>
              )}

              <div className="flex gap-4">
                {/* Icon */}
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: type.bgColor }}
                >
                  <Icon className="w-7 h-7" style={{ color: type.color }} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#4A2C0A]">
                      {type.title}
                    </h3>
                    {isSelected && (
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: type.color }}
                      >
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-[#4A2C0A]/60 font-['Poppins'] mt-0.5">
                    {type.subtitle}
                  </p>
                  <p className="text-sm text-[#4A2C0A]/80 font-['Poppins'] mt-2">
                    {type.description}
                  </p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {type.features.map((feature, i) => (
                      <div
                        key={i}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium font-['Poppins']"
                        style={{ 
                          backgroundColor: type.bgColor,
                          color: type.color 
                        }}
                      >
                        <feature.icon className="w-3.5 h-3.5" />
                        {feature.text}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Comparison Note */}
      <div className="bg-[#F5EBD7] rounded-xl p-4 text-center">
        <p className="text-sm text-[#4A2C0A]/70 font-['Poppins']">
          <strong className="text-[#4A2C0A]">¿No sabes cuál elegir?</strong><br />
          El vendedor <strong>Carrito</strong> tiene mayores ganancias pero requiere 
          disponibilidad física. El vendedor <strong>Digital</strong> es ideal si 
          prefieres horarios flexibles.
        </p>
      </div>

      {/* Continue Button */}
      <Button
        onClick={handleContinue}
        disabled={!selectedType}
        className={`w-full h-14 text-lg font-semibold rounded-2xl font-['Poppins'] transition-all ${
          selectedType
            ? "bg-[#C8902A] hover:bg-[#A67720] text-white shadow-lg shadow-[#C8902A]/30"
            : "bg-[#E5D4B3] text-[#4A2C0A]/40 cursor-not-allowed"
        }`}
        data-testid="btn-continue-tipo"
      >
        Continuar
        <ChevronRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  );
}
